import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@clarix/ui';
import {
  useSelfServeCreateWorkspace,
  useProvisioningStatus,
  useResolveWorkspace,
  useMasters,
  getApiErrorMessage,
} from '@/core/api';
import { PasswordInput } from './PasswordInput';
import type { MasterRow } from '@clarix/shared-types';

/**
 * Step 3 of self-serve signup: workspace + owner details, submitted together
 * (the owner account is created atomically, inside the new tenant database,
 * by POST /workspaces — there is no separate account-creation step). On
 * success, waits for real tenant provisioning to finish, then hands off to
 * the ordinary login form rather than re-implementing session issuance and
 * the 2FA/forced-enrolment/password-change gates a second time here.
 *
 * Requires router state from VerifyOtpPage ({ email, emailVerificationToken })
 * — a direct visit here has no verified email to build on, so it bounces to
 * /signup, mirroring VerifyOtpPage's own guard.
 */

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

const SLUG_DEBOUNCE_MS = 350;

function MasterSelect({
  id,
  rows,
  loading,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  rows: MasterRow[] | undefined;
  loading: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={loading || !rows?.length}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={loading ? 'Loading…' : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {(rows ?? []).map((row) => (
          <SelectItem key={row.id} value={row.code}>
            {row.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type Phase = 'form' | 'provisioning';

export function SignupWorkspacePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { email?: string; emailVerificationToken?: string } | null;
  const email = state?.email ?? '';
  const emailVerificationToken = state?.emailVerificationToken ?? '';

  useEffect(() => {
    if (!email || !emailVerificationToken) navigate('/signup', { replace: true });
  }, [email, emailVerificationToken, navigate]);

  const create = useSelfServeCreateWorkspace();
  const industries = useMasters('industry');
  const teamSizes = useMasters('team_size_range');

  const [phase, setPhase] = useState<Phase>('form');
  const [slug, setSlug] = useState<string | null>(null);
  const [ownerPassword, setOwnerPassword] = useState('');

  // Workspace identity
  const [workspaceName, setWorkspaceName] = useState('');
  const [slugState, setSlugState] = useState('');
  const [slugDirty, setSlugDirty] = useState(false);
  const derivedSlug = useMemo(() => slugify(workspaceName), [workspaceName]);
  const slugInput = slugDirty ? slugState : derivedSlug;
  const [debouncedSlug, setDebouncedSlug] = useState(slugInput);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSlug(slugInput), SLUG_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [slugInput]);
  // PUBLIC check — no session exists yet, unlike the authenticated
  // check-slug endpoint OnboardingPage.tsx uses.
  const resolved = useResolveWorkspace(debouncedSlug);
  const slugStatus: 'idle' | 'checking' | 'available' | 'taken' =
    !debouncedSlug || debouncedSlug.length < 2
      ? 'idle'
      : resolved.isFetching
        ? 'checking'
        : resolved.data
          ? resolved.data.exists
            ? 'taken'
            : 'available'
          : 'idle';
  const slugHint =
    slugStatus === 'checking'
      ? 'Checking availability…'
      : slugStatus === 'available'
        ? 'Available'
        : slugStatus === 'taken'
          ? 'That URL is already taken.'
          : 'Lowercase letters, digits, and hyphens. 2–63 characters.';

  // Org profile
  const [industry, setIndustry] = useState('');
  const [teamSize, setTeamSize] = useState('');

  // Owner
  const [ownerName, setOwnerName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const defaultTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
    } catch {
      return 'Asia/Kolkata';
    }
  }, []);
  const defaultLocale = (typeof navigator !== 'undefined' && navigator.language) || 'en-IN';

  const passwordsMatch = ownerPassword.length > 0 && ownerPassword === confirmPassword;
  const canSubmit =
    !create.isPending &&
    workspaceName.trim().length >= 2 &&
    slugInput.trim().length >= 2 &&
    slugStatus === 'available' &&
    !!industry &&
    !!teamSize &&
    ownerName.trim().length >= 1 &&
    ownerPassword.length >= 8 &&
    passwordsMatch;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      const result = await create.mutateAsync({
        slug: slugInput.trim(),
        name: workspaceName.trim(),
        workspace_name: workspaceName.trim(),
        industry,
        timezone: defaultTimezone,
        locale: defaultLocale,
        currency: 'INR',
        team_size: teamSize,
        owner_name: ownerName.trim(),
        owner_email: email,
        owner_password: ownerPassword,
        email_verification_token: emailVerificationToken,
      });
      setSlug(result.slug);
      setPhase('provisioning');
    } catch {
      /* error surfaced below */
    }
  };

  const status = useProvisioningStatus(slug, { enabled: phase === 'provisioning' });
  const provisioningFailed = phase === 'provisioning' && status.data?.status === 'FAILED';

  useEffect(() => {
    if (status.data?.status !== 'ACTIVE') return;
    // Hand off to the ordinary login form rather than re-implementing session
    // issuance and the 2FA/enrolment/password gates here — the owner just set
    // this password seconds ago, so re-entering it once is a small cost for
    // reusing already-hardened sign-in logic.
    navigate(
      `/login?workspace=${encodeURIComponent(slug ?? '')}&email=${encodeURIComponent(email)}`,
      { replace: true },
    );
  }, [status.data?.status, slug, email, navigate]);

  const createError = create.error ? getApiErrorMessage(create.error) : null;

  if (phase === 'provisioning') {
    return (
      <div className="rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
        {provisioningFailed ? (
          <>
            <h2 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
              Something went wrong
            </h2>
            <p className="mt-2 text-xl text-slate-500">
              We couldn&apos;t finish setting up &quot;{slug}&quot;. Please try again, or contact
              support if this keeps happening.
            </p>
            <Button className="mt-4" onClick={() => setPhase('form')}>
              Try again
            </Button>
          </>
        ) : (
          <>
            <h2 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
              Creating your workspace…
            </h2>
            <p className="mt-2 text-xl text-slate-500">
              This takes a minute — we&apos;re setting up &quot;{slug}&quot; for you.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
      <h1 className="text-4xl font-semibold text-slate-900 dark:text-slate-100">
        Set up your workspace
      </h1>
      <p className="mt-1 text-lg text-slate-500">Signed in as {email}.</p>

      <form className="mt-6 space-y-6" onSubmit={onSubmit} noValidate>
        <div className="space-y-4">
          <div>
            <Label htmlFor="workspace_name">Workspace name</Label>
            <Input
              id="workspace_name"
              autoFocus
              required
              placeholder="Sarvadhi Solutions Pvt Ltd"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="slug">Workspace URL</Label>
            <div className="flex overflow-hidden rounded-md ring-1 ring-slate-300 focus-within:ring-2 focus-within:ring-indigo-500 dark:ring-slate-700">
              <Input
                id="slug"
                type="text"
                pattern="^[a-z][a-z0-9-]{1,39}$"
                required
                placeholder={derivedSlug || 'your-workspace'}
                value={slugInput}
                onChange={(e) => {
                  setSlugState(e.target.value);
                  setSlugDirty(true);
                }}
                className="rounded-none border-0 ring-0 focus:ring-0"
              />
              <span className="flex items-center bg-slate-50 px-2 text-lg text-slate-500 dark:bg-slate-900">
                .clarix.com
              </span>
            </div>
            <p
              className={`mt-1 text-sm leading-tight ${
                slugStatus === 'available'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : slugStatus === 'taken'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-slate-500'
              }`}
            >
              {slugHint}
            </p>
          </div>

          <div>
            <Label htmlFor="industry">Industry</Label>
            <MasterSelect
              id="industry"
              rows={industries.data?.rows}
              loading={industries.isLoading}
              value={industry}
              onChange={setIndustry}
              placeholder="Select your industry"
            />
          </div>

          <div>
            <Label htmlFor="team_size">Team size</Label>
            <MasterSelect
              id="team_size"
              rows={teamSizes.data?.rows}
              loading={teamSizes.isLoading}
              value={teamSize}
              onChange={setTeamSize}
              placeholder="Select team size"
            />
          </div>
        </div>

        <div className="space-y-4 border-t border-slate-200 pt-4 dark:border-slate-800">
          <div>
            <Label htmlFor="owner_name">Your name</Label>
            <Input
              id="owner_name"
              autoComplete="name"
              required
              placeholder="Jane Doe"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="owner_password">Password</Label>
            <PasswordInput
              id="owner_password"
              autoComplete="new-password"
              required
              minLength={8}
              placeholder="At least 8 characters"
              value={ownerPassword}
              onChange={(e) => setOwnerPassword(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="confirm_password">Confirm password</Label>
            <PasswordInput
              id="confirm_password"
              autoComplete="new-password"
              required
              minLength={8}
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="mt-1 text-lg text-red-600 dark:text-red-400">
                Passwords don&apos;t match.
              </p>
            )}
          </div>
        </div>

        {createError && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-xl text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {createError}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={!canSubmit}>
          {create.isPending ? 'Creating workspace…' : 'Create workspace'}
        </Button>
      </form>
    </div>
  );
}
